import type { AlertMetric, Severity } from "../incidents/incidents";

/** The settings an organization-wide rule starts from, minus who it notifies. */
export interface DefaultAlertRuleSettings {
  minValue: number | null;
  maxValue: number | null;
  durationSeconds: number;
  cooldownSeconds: number;
  severity: Severity;
}

/** The cooldown every default rule starts with: thirty minutes between two alerts. */
export const DEFAULT_ALERT_COOLDOWN_SECONDS = 1_800;

/**
 * The organization-wide rules every organization gets (ADR-092).
 *
 * One table read by the development seed, by organization creation, by the
 * "create default rules" action and by the create dialog's prefill, so the
 * four cannot drift apart. Keyed by metric so a new metric does not compile
 * until somebody decides its default.
 *
 * Bounds are null unless stated: temperature, humidity and distance defer to
 * the asset's target range (ADR-048), and the bit, schedule and silence
 * metrics have no range to carry.
 */
export const DEFAULT_ALERT_RULES: Readonly<Record<AlertMetric, DefaultAlertRuleSettings>> = {
  // Ten minutes: long enough that restocking a freezer does not raise an
  // alarm, short enough to matter for perishable stock.
  TEMPERATURE: {
    minValue: null,
    maxValue: null,
    durationSeconds: 600,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "CRITICAL",
  },
  HUMIDITY: {
    minValue: null,
    maxValue: null,
    durationSeconds: 1_800,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "WARNING",
  },
  // The one threshold that belongs on the rule: a battery floor is a property
  // of the sensor, and no asset has an opinion about it.
  BATTERY: {
    minValue: 15,
    maxValue: null,
    durationSeconds: 3_600,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "WARNING",
  },
  // Three missed uplinks at a five-minute interval (ADR-094).
  CONNECTIVITY: {
    minValue: null,
    maxValue: null,
    durationSeconds: 900,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "CRITICAL",
  },
  // Thirty minutes, because filling or drawing down a tank crosses a bound in
  // the normal course of work (ADR-064). WARNING: an empty tank is a
  // restocking problem. The EM410-RDL reporting interval is still to verify.
  DISTANCE: {
    minValue: null,
    maxValue: null,
    durationSeconds: 1_800,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "WARNING",
  },
  // Judged against the asset's expected-occupancy hours (ADR-069). Ten minutes
  // is the defence against a guard or a cleaner walking past; WARNING because
  // that is the common case. Whether the VS370 reports on change or on an
  // interval is still to verify.
  PRESENCE: {
    minValue: null,
    maxValue: null,
    durationSeconds: 600,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "WARNING",
  },
  // Two minutes: a splash that evaporates should not page someone, a standing
  // puddle should. Unverified against a real EM300-SLD interval (ADR-077).
  LEAK: {
    minValue: null,
    maxValue: null,
    durationSeconds: 120,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "CRITICAL",
  },
  // Five minutes: a cold-room door left ajar should page before the chamber
  // warms. The units tested report on that same interval (ADR-100).
  DOOR: {
    minValue: null,
    maxValue: null,
    durationSeconds: 300,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "WARNING",
  },
  // Recipient carriers for controller channels (ADR-082): the timers stored
  // here are never read, because each DeviceChannel carries its own.
  CHANNEL_RANGE: {
    minValue: null,
    maxValue: null,
    durationSeconds: 600,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "WARNING",
  },
  CHANNEL_ACTIVE: {
    minValue: null,
    maxValue: null,
    durationSeconds: 300,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "WARNING",
  },
  // Ten minutes: a motor start should not page, and the CT103 guide's example
  // interval is ten minutes. Bounds stay empty so each circuit uses its own
  // ampere band. WARNING: a current leaving its band is a load problem, not
  // a cold-chain loss (ADR-102).
  CURRENT: {
    minValue: null,
    maxValue: null,
    durationSeconds: 600,
    cooldownSeconds: DEFAULT_ALERT_COOLDOWN_SECONDS,
    severity: "WARNING",
  },
};
